import { Link } from "react-router";
import { useTranslations } from "use-intl";
import { useState, useTransition } from "react";
import { useNavigate } from "react-router";
import { ArrowDown, ArrowUp, ChevronDown, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  addWorkoutExercise,
  deleteWorkout,
  moveWorkoutExercise,
  removeWorkoutExercise,
  updateWorkout,
  updateWorkoutExercise,
} from "@/lib/actions";
import { EditLocaleToggle, useEditLocale } from "@/components/app/edit-locale";
import { Section } from "@/components/app/page-header";
import { Stepper } from "@/components/app/stepper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ExerciseType, LocalizedText } from "@liftquest/shared/types";
import type { Locale } from "@liftquest/shared/localized";
import { cn } from "@/lib/utils";

type Item = {
  id: string;
  exerciseId: string;
  name: string;
  type: ExerciseType;
  sets: number;
  targetMin: number;
  targetMax: number;
  restSec: number;
  warmupSets: number;
  alternativeExerciseId: string | null;
  alternativeNote: LocalizedText;
  notes: LocalizedText;
};

type LibraryEntry = { id: string; name: string; type: ExerciseType; muscles: string[] };

export function WorkoutEditor({
  planId,
  workout,
  items,
  library,
}: {
  planId: string;
  workout: { id: string; name: LocalizedText; focus: LocalizedText; kind: "strength" | "cardio" };
  items: Item[];
  library: LibraryEntry[];
}) {
  const t = useTranslations("workoutEditor");
  const tp = useTranslations("planEditor");
  const tc = useTranslations("common");
  const [editLocale, setEditLocale] = useEditLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pending, start] = useTransition();
  const navigate = useNavigate();

  return (
    <div className="mt-4 space-y-2">
      <EditLocaleToggle value={editLocale} onChange={setEditLocale} />
      <WorkoutMeta key={editLocale} workout={workout} locale={editLocale} />

      <Section title={t("exercises")}>
        <ul className="space-y-2">
          {items.map((it, i) => (
            <ItemCard
              key={`${it.id}-${editLocale}`}
              item={it}
              index={i}
              count={items.length}
              locale={editLocale}
              library={library}
            />
          ))}
          {items.length === 0 && (
            <li className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t("empty")}
            </li>
          )}
        </ul>
        <Button size="lg" className="mt-3 w-full" disabled={pending} onClick={() => setPickerOpen(true)}>
          <Plus /> {t("addExercise")}
        </Button>
      </Section>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" className="mt-8 w-full">
            <Trash2 /> {tp("deleteWorkout")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tc("deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{tc("deleteConfirmText")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("cancel")}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() =>
                start(async () => {
                  await deleteWorkout(workout.id);
                  navigate(`/plans/${planId}`, { replace: true });
                })
              }>
              {tc("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExercisePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        library={library}
        createHref={`/exercises/new?returnTo=${encodeURIComponent(`/plans/${planId}/workouts/${workout.id}`)}`}
        onPick={(id) =>
          start(async () => {
            await addWorkoutExercise(workout.id, id);
            setPickerOpen(false);
          })
        }
      />
    </div>
  );
}

function WorkoutMeta({
  workout,
  locale,
}: {
  workout: { id: string; name: LocalizedText; focus: LocalizedText; kind: "strength" | "cardio" };
  locale: Locale;
}) {
  const t = useTranslations("planEditor");
  const tc = useTranslations("common");
  const [name, setName] = useState(workout.name[locale] ?? "");
  const [focus, setFocus] = useState(workout.focus[locale] ?? "");
  const [kind, setKind] = useState(workout.kind);
  const [pending, start] = useTransition();
  const dirty = name !== (workout.name[locale] ?? "") || focus !== (workout.focus[locale] ?? "") || kind !== workout.kind;

  return (
    <Card className="py-4">
      <CardContent className="space-y-3 px-4">
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="w-name">{t("name")}</Label>
            <Input id="w-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 text-base" />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("kind")}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as "strength" | "cardio")}>
              <SelectTrigger className="h-11! w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="strength">{t("kinds.strength")}</SelectItem>
                <SelectItem value="cardio">{t("kinds.cardio")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="w-focus">{t("focus")}</Label>
          <Input id="w-focus" value={focus} onChange={(e) => setFocus(e.target.value)} className="h-11 text-base" />
        </div>
        {dirty && (
          <Button
            className="w-full"
            disabled={pending}
            onClick={() =>
              start(async () => {
                await updateWorkout(workout.id, {
                  name: { ...workout.name, [locale]: name },
                  focus: { ...workout.focus, [locale]: focus },
                  kind,
                });
                toast.success(tc("saved"));
              })
            }
          >
            {pending && <Loader2 className="animate-spin" />} {tc("save")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ItemCard({
  item,
  index,
  count,
  locale,
  library,
}: {
  item: Item;
  index: number;
  count: number;
  locale: Locale;
  library: LibraryEntry[];
}) {
  const t = useTranslations("workoutEditor");
  const tc = useTranslations("common");
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({
    sets: item.sets,
    targetMin: item.targetMin,
    targetMax: item.targetMax,
    restSec: item.restSec,
    warmupSets: item.warmupSets,
    alternativeExerciseId: item.alternativeExerciseId,
    alternativeNote: item.alternativeNote[locale] ?? "",
    notes: item.notes[locale] ?? "",
  });
  const [pending, start] = useTransition();
  const unit = item.type === "time" ? " s" : item.type === "cardio" ? " min" : "";

  function save() {
    start(async () => {
      try {
        await updateWorkoutExercise(item.id, {
          sets: d.sets,
          targetMin: Math.min(d.targetMin, d.targetMax),
          targetMax: Math.max(d.targetMin, d.targetMax),
          restSec: d.restSec,
          warmupSets: d.warmupSets,
          alternativeExerciseId: d.alternativeExerciseId,
          alternativeNote: { ...item.alternativeNote, [locale]: d.alternativeNote },
          notes: { ...item.notes, [locale]: d.notes },
        });
        toast.success(tc("saved"));
        setOpen(false);
      } catch {
        toast.error(tc("error"));
      }
    });
  }

  return (
    <li>
      <Card className="gap-0 py-0">
        <div className="flex items-center">
          <button type="button" className="flex min-w-0 flex-1 items-center gap-3 p-3.5 text-left" onClick={() => setOpen((o) => !o)}>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {index + 1}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{item.name}</span>
              <span className="block text-xs tabular-nums text-muted-foreground">
                {item.type === "cardio" ? "" : `${item.sets} × `}
                {item.targetMin}–{item.targetMax}
                {unit}
                {item.restSec > 0 && ` · ${item.restSec} s`}
              </span>
            </span>
            <ChevronDown className={cn("size-5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
          </button>
          <div className="flex flex-col border-l">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={index === 0 || pending}
              aria-label={tc("moveUp")}
              onClick={() => start(() => moveWorkoutExercise(item.id, "up"))}
            >
              <ArrowUp />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={index === count - 1 || pending}
              aria-label={tc("moveDown")}
              onClick={() => start(() => moveWorkoutExercise(item.id, "down"))}
            >
              <ArrowDown />
            </Button>
          </div>
        </div>
        {open && (
          <CardContent className="space-y-3 border-t px-3.5 py-3.5">
            <div className="grid grid-cols-2 gap-2">
              {item.type !== "cardio" && (
                <Stepper size="sm" label={t("sets")} value={d.sets} min={1} max={20} onChange={(v) => setD({ ...d, sets: v ?? 1 })} />
              )}
              <Stepper
                size="sm"
                label={t("rest")}
                value={d.restSec}
                step={15}
                max={600}
                onChange={(v) => setD({ ...d, restSec: v ?? 0 })}
              />
              <Stepper
                size="sm"
                label={`${t(`range.${item.type}`)} ${t("from")}`}
                value={d.targetMin}
                step={item.type === "time" ? 5 : 1}
                onChange={(v) => setD({ ...d, targetMin: v ?? 0 })}
              />
              <Stepper
                size="sm"
                label={`${t(`range.${item.type}`)} ${t("to")}`}
                value={d.targetMax}
                step={item.type === "time" ? 5 : 1}
                onChange={(v) => setD({ ...d, targetMax: v ?? 0 })}
              />
              {item.type === "reps" && (
                <Stepper
                  size="sm"
                  label={t("warmupSets")}
                  value={d.warmupSets}
                  max={5}
                  onChange={(v) => setD({ ...d, warmupSets: v ?? 0 })}
                />
              )}
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">{t("alternative")}</Label>
              <Input value={d.alternativeNote} onChange={(e) => setD({ ...d, alternativeNote: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">{t("alternativeExercise")}</Label>
              <Select
                value={d.alternativeExerciseId ?? "none"}
                onValueChange={(v) => setD({ ...d, alternativeExerciseId: v === "none" ? null : v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{tc("none")}</SelectItem>
                  {library
                    .filter((e) => e.id !== item.exerciseId)
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">{t("notes")}</Label>
              <Input value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                disabled={pending}
                onClick={() => start(() => removeWorkoutExercise(item.id))}
              >
                <Trash2 /> {t("remove")}
              </Button>
              <Button className="flex-1" disabled={pending} onClick={save}>
                {pending && <Loader2 className="animate-spin" />} {tc("save")}
              </Button>
            </div>
            <Link to={`/exercises/${item.exerciseId}`} className="block text-center text-xs font-medium text-primary">
              {tc("edit")}: {item.name}
            </Link>
          </CardContent>
        )}
      </Card>
    </li>
  );
}

function ExercisePicker({
  open,
  onOpenChange,
  library,
  createHref,
  onPick,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  library: LibraryEntry[];
  createHref: string;
  onPick: (id: string) => void;
}) {
  const t = useTranslations("workoutEditor");
  const te = useTranslations("exercises");
  const tm = useTranslations("muscles");
  const [q, setQ] = useState("");
  const list = library.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90dvh]">
        <DrawerHeader className="text-left">
          <DrawerTitle>{t("pickExercise")}</DrawerTitle>
        </DrawerHeader>
        <div className="space-y-3 overflow-y-auto px-4 pb-8">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={te("search")} className="h-11 pl-9 text-base" />
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to={createHref}>
              <Plus /> {t("createNew")}
            </Link>
          </Button>
          <ul className="divide-y rounded-2xl border">
            {list.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => onPick(e.id)}
                  className="flex w-full flex-col px-3.5 py-3 text-left hover:bg-muted/60"
                >
                  <span className="font-medium">{e.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {e.muscles.map((m) => (tm.has(m as "abs") ? tm(m as "abs") : m)).join(", ")}
                  </span>
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="px-3.5 py-3 text-sm text-muted-foreground">{te("empty")}</li>}
          </ul>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
